import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Libraries, LoadScript, StandaloneSearchBox } from "@react-google-maps/api";

/* ─────────────────────────────────────────
   Props interface
───────────────────────────────────────── */
interface CreateMagasiEnligneProps {
  adress: string;
  setAdress: (v: string) => void;
  Latitude: any;
  setLatitude: (v: any) => void;
  Longitude: any;
  setLongitude: (v: any) => void;
  NomMagasin: string;
  setNomMagasin: (v: string) => void;
  nom: string;
  setnom: (v: string) => void;
  email: string;
  setemail: (v: string) => void;
  password: string;
  setpassword: (v: string) => void;
  tele: string;
  settele: (v: string) => void;
  /* colis state lives in parent */
  ReceptionClois: boolean;
  setReceptionClois: (v: boolean) => void;
  messageError: string;
  setMessageError: (v: string) => void;
  messageSucess: string;
  setMessageSucess: (v: string) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  sendingOtp?: boolean;
}

/* ─────────────────────────────────────────
   Component
───────────────────────────────────────── */
const CreateMagasiEnligne: React.FC<CreateMagasiEnligneProps> = ({
  adress, setAdress,
  Latitude, setLatitude,
  Longitude, setLongitude,
  NomMagasin, setNomMagasin,
  nom, setnom,
  email, setemail,
  password, setpassword,
  tele, settele,
  ReceptionClois, setReceptionClois,
  messageError, setMessageError,
  messageSucess, setMessageSucess,
  handleSubmit,
  sendingOtp,
}) => {
  const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null);
  const inputRef     = useRef<HTMLInputElement>(null);

  /* local UI state only */
  const [showPassword, setShowPassword]               = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const libraries: Libraries = ["places"];

  const handlePlaceChanged = () => {
    const places = searchBoxRef.current?.getPlaces();
    if (places && places.length > 0) {
      const place = places[0];
      setAdress(place.formatted_address || "");
      if (place.geometry?.location) {
        setLatitude(place.geometry.location.lat());
        setLongitude(place.geometry.location.lng());
      }
    }
  };

  /* ─── Inline styles ─────────────────────────────────────────────────── */
  const s = {
    container: {
      display: "flex", height: "100vh",
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      overflow: "hidden",
    } as React.CSSProperties,

    heroSection: {
      flex: "1 1 40%",
      background: "url('/images/bg-01.jpg') no-repeat center center",
      backgroundSize: "cover",
      display: "flex", justifyContent: "center", alignItems: "center",
      position: "relative" as const, overflow: "hidden",
    },
    heroOverlay: {
      position: "absolute" as const, inset: 0,
      background: "rgba(26,25,20,0.87)",
    },
    heroContent: {
      textAlign: "center" as const, zIndex: 1,
      padding: "40px", position: "relative" as const,
    },
    heroTitle: {
      fontFamily: "'Outfit', sans-serif", fontWeight: 700,
      fontSize: "4rem", color: "#ffffff", marginBottom: "24px",
      letterSpacing: "-0.02em", textShadow: "0 4px 20px rgba(0,0,0,0.5)",
    },
    heroSubtitle: {
      fontFamily: "'Outfit', sans-serif", fontWeight: 400,
      fontSize: "1.15rem", lineHeight: "1.8",
      color: "rgba(255,255,255,0.95)", maxWidth: "500px", margin: "0 auto",
      textShadow: "0 2px 10px rgba(0,0,0,0.3)",
    },

    formSection: {
      flex: "1 1 60%", background: "#ffffff",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "40px", overflowY: "auto" as const,
      boxShadow: "-10px 0 30px rgba(0,0,0,0.1)",
    },
    formContainer: {
      width: "100%", maxWidth: "700px",
      padding: "40px", position: "relative" as const,
    },
    backButton: {
      position: "absolute" as const, top: "20px", left: "20px",
      width: "44px", height: "44px",
      background: "#f3f4f6", border: "none", borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer", fontSize: "1.8rem", color: "#6b7280",
      transition: "all 0.3s ease",
    },
    formTitle: {
      fontSize: "2rem", fontWeight: 700, color: "#1a1a1a",
      marginBottom: "8px", textAlign: "center" as const,
      fontFamily: "'Outfit', sans-serif",
    },
    formSubtitle: {
      fontSize: "1rem", color: "#6b7280",
      marginBottom: "40px", textAlign: "center" as const,
    },
    divider: {
      width: "80px", height: "4px",
      background: "linear-gradient(90deg,#ffc400,transparent)",
      margin: "12px auto 32px auto",
    },

    alertSuccess: {
      background: "#d1fae5", border: "2px solid #059669",
      borderRadius: "12px", padding: "16px 20px", marginBottom: "24px",
      display: "flex", alignItems: "center", gap: "12px",
      color: "#059669", fontWeight: 500,
    },
    alertDanger: {
      background: "#fee2e2", border: "2px solid #dc2626",
      borderRadius: "12px", padding: "16px 20px", marginBottom: "24px",
      color: "#dc2626", fontWeight: 500,
    },

    row: {
      display: "grid", gridTemplateColumns: "repeat(2,1fr)",
      gap: "24px", marginBottom: "24px",
    },
    fullWidth: { gridColumn: "1 / -1" } as React.CSSProperties,

    label: {
      display: "block", fontSize: "0.9rem", fontWeight: 600,
      color: "#1a1a1a", marginBottom: "8px",
      fontFamily: "'Outfit', sans-serif",
    } as React.CSSProperties,

    inputWrapper: { position: "relative" as const },

    input: {
      width: "100%", padding: "14px 16px",
      border: "2px solid #e5e7eb", borderRadius: "12px",
      fontSize: "0.95rem", fontFamily: "'Outfit', sans-serif",
      fontWeight: 500, color: "#1a1a1a", background: "#ffffff",
      transition: "all 0.3s ease", boxSizing: "border-box" as const,
    },
    inputPassword: { paddingRight: "50px" },
    inputWithIcon: { paddingLeft: "48px" },

    eyeBtn: {
      position: "absolute" as const, right: "16px", top: "50%",
      transform: "translateY(-50%)",
      background: "none", border: "none", padding: 0, cursor: "pointer",
      color: "#6b7280", fontSize: "1.2rem",
      display: "flex", alignItems: "center", transition: "color 0.3s ease",
    },
    locationIcon: {
      position: "absolute" as const, left: "16px", top: "50%",
      transform: "translateY(-50%)",
      color: "#6b7280", fontSize: "1.1rem", pointerEvents: "none" as const,
    },

    submitButton: {
      width: "100%", padding: "16px",
      background: "linear-gradient(135deg,#ffc400 0%,#e6b000 100%)",
      border: "none", borderRadius: "12px",
      fontSize: "1.05rem", fontWeight: 700, color: "#1a1a1a",
      cursor: "pointer", transition: "all 0.3s ease",
      boxShadow: "0 4px 6px -1px rgba(255,196,0,0.3)",
      fontFamily: "'Outfit', sans-serif", marginTop: "24px",
    },
    linkContainer: { marginTop: "24px", textAlign: "center" as const },
    link: { color: "#2563eb", textDecoration: "none", fontWeight: 500, fontSize: "0.95rem" },
  };

  const globalCss = `
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

    input:focus {
      outline: none !important;
      border-color: #ffc400 !important;
      box-shadow: 0 0 0 3px rgba(255,196,0,0.1) !important;
    }
    .eye-hover:hover  { color: #1a1a1a !important; }
    .back-btn-h:hover { background: #e5e7eb !important; transform: scale(1.1); }

    .colis-row {
      display: flex; align-items: center; gap: 16px;
      padding: 18px 20px;
      border: 2px solid #e5e7eb; border-radius: 12px;
      background: #fafafa; cursor: pointer;
      transition: border-color 0.3s, background 0.3s;
      user-select: none;
    }
    .colis-row:hover, .colis-row.on { border-color: #ffc400; background: #fffbeb; }

    .colis-icon {
      width: 44px; height: 44px; border-radius: 10px;
      background: #f3f4f6; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
      font-size: 1.35rem; color: #9ca3af;
      transition: background 0.3s, color 0.3s;
    }
    .colis-row.on .colis-icon { background: rgba(255,196,0,0.15); color: #b8860b; }

    .colis-text { flex: 1; }
    .colis-text strong {
      display: block; font-size: 0.95rem; font-weight: 600;
      color: #1a1a1a; font-family: 'Outfit', sans-serif;
    }
    .colis-text span { font-size: 0.82rem; color: #6b7280; }

    .sw { position: relative; display: inline-block; width: 48px; height: 26px; flex-shrink: 0; }
    .sw input { opacity: 0; width: 0; height: 0; }
    .sw-track {
      position: absolute; inset: 0; background: #d1d5db;
      border-radius: 34px; cursor: pointer;
      transition: background 0.3s;
    }
    .sw-track::before {
      content: ''; position: absolute;
      height: 20px; width: 20px; left: 3px; bottom: 3px;
      background: #fff; border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      transition: transform 0.3s;
    }
    .sw input:checked + .sw-track { background: #ffc400; }
    .sw input:checked + .sw-track::before { transform: translateX(22px); }

    .magasin-loader-spinner {
      display: inline-block;
      width: 20px;
      height: 20px;
      border: 2px solid rgba(26, 26, 26, 0.3);
      border-top: 2px solid #1a1a1a;
      border-radius: 50%;
      animation: spin-magasin 0.8s linear infinite;
      vertical-align: middle;
    }
    @keyframes spin-magasin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  /* ────────────────────────────────────────────────────────────────────── */
  return (
    <>
      <style>{globalCss}</style>
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"
      />

      <div style={s.container}>

        {/* ── Hero ──────────────────────────────────────────────────── */}
        <div style={s.heroSection}>
          <div style={s.heroOverlay} />
          <div style={s.heroContent}>
            <h1 style={s.heroTitle}>Wyn</h1>
            <p style={s.heroSubtitle}>
              Libérer l'efficacité : là où la précision mécanique rencontre le contrôle numérique
            </p>
          </div>
        </div>

        {/* ── Form ──────────────────────────────────────────────────── */}
        <div style={s.formSection}>
          <div style={s.formContainer}>

            <button
              type="button"
              onClick={() => window.history.back()}
              style={s.backButton}
              className="back-btn-h"
            >
              <i className="bi bi-arrow-left" />
            </button>

            <form onSubmit={handleSubmit}>
              <h2 style={s.formTitle}>Créer votre magasin en ligne</h2>
              <div style={s.divider} />
              <p style={s.formSubtitle}>Rejoignez notre plateforme et développez votre activité</p>

              {/* Alerts */}
              {messageSucess && (
                <div style={s.alertSuccess}>
                  <i className="bi bi-check-circle-fill" style={{ fontSize: "1.25rem" }} />
                  <span>{messageSucess}</span>
                </div>
              )}
              {messageError && messageError !== " " && (
                <div style={s.alertDanger}>
                  <strong>Erreur</strong>
                  <hr style={{ margin: "8px 0", border: "none", borderTop: "1px solid rgba(220,38,38,0.2)" }} />
                  <p style={{ margin: 0 }}>{messageError}</p>
                </div>
              )}

              <div style={s.row}>

                {/* Nom complet */}
                <div>
                  <label style={s.label}>Nom Complet</label>
                  <div style={s.inputWrapper}>
                    <input
                      type="text"
                      value={nom}
                      onChange={(e) => setnom(e.target.value)}
                      placeholder="Entrez votre nom complet"
                      style={s.input}
                    />
                  </div>
                </div>

                {/* Nom magasin */}
                <div>
                  <label style={s.label}>Nom du Magasin</label>
                  <div style={s.inputWrapper}>
                    <input
                      type="text"
                      value={NomMagasin}
                      onChange={(e) => setNomMagasin(e.target.value)}
                      placeholder="Nom de votre magasin"
                      style={s.input}
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label style={s.label}>Adresse E-mail</label>
                  <div style={s.inputWrapper}>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setemail(e.target.value)}
                      placeholder="votre@email.com"
                      style={s.input}
                    />
                  </div>
                </div>

                {/* Téléphone */}
                <div>
                  <label style={s.label}>Numéro de Téléphone</label>
                  <div style={s.inputWrapper}>
                    <input
                      type="tel"
                      value={tele}
                      onChange={(e) => settele(e.target.value)}
                      placeholder="+212 6XX XXX XXX"
                      style={s.input}
                    />
                  </div>
                </div>

                {/* Localisation — full width */}
                <div style={s.fullWidth}>
                  <label style={s.label}>Localisation</label>
                  <div style={s.inputWrapper}>
                    <i className="bi bi-geo-alt-fill" style={s.locationIcon} />
                    <LoadScript
                      googleMapsApiKey="AIzaSyBmLZdgzjt2mfMeAb-iZTkUeNYWqPU1FoE"
                      libraries={libraries}
                    >
                      <StandaloneSearchBox
                        onLoad={(ref) => (searchBoxRef.current = ref)}
                        onPlacesChanged={handlePlaceChanged}
                      >
                        <input
                          ref={inputRef}
                          type="text"
                          placeholder="Rechercher votre adresse..."
                          style={{ ...s.input, ...s.inputWithIcon }}
                        />
                      </StandaloneSearchBox>
                    </LoadScript>
                  </div>
                </div>

                {/* Mot de passe */}
                <div>
                  <label style={s.label}>Mot de Passe</label>
                  <div style={s.inputWrapper}>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setpassword(e.target.value)}
                      placeholder="••••••••"
                      style={{ ...s.input, ...s.inputPassword }}
                    />
                    <button
                      type="button"
                      className="eye-hover"
                      style={s.eyeBtn}
                      onClick={() => setShowPassword((p) => !p)}
                    >
                      <i className={`bi ${showPassword ? "bi-eye-fill" : "bi-eye-slash-fill"}`} />
                    </button>
                  </div>
                </div>

                {/* Confirmer mot de passe */}
                <div>
                  <label style={s.label}>Confirmer le Mot de Passe</label>
                  <div style={s.inputWrapper}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      style={{ ...s.input, ...s.inputPassword }}
                    />
                    <button
                      type="button"
                      className="eye-hover"
                      style={s.eyeBtn}
                      onClick={() => setShowConfirmPassword((p) => !p)}
                    >
                      <i className={`bi ${showConfirmPassword ? "bi-eye-fill" : "bi-eye-slash-fill"}`} />
                    </button>
                  </div>
                </div>

                {/* ── Réception des colis — full width ─────────────── */}
                <div style={s.fullWidth}>
                  <label style={s.label}>Options du magasin</label>

                  <div
                    className={`colis-row${ReceptionClois ? " on" : ""}`}
                    onClick={() => setReceptionClois(!ReceptionClois)}
                  >
                    {/* Icon */}
                    <div className="colis-icon">
                      <i className="bi bi-box-seam" />
                    </div>

                    {/* Text */}
                    <div className="colis-text">
                      <strong>Accepter la réception des colis</strong>
                      <span>
                        {ReceptionClois
                          ? "Votre magasin accepte la livraison de colis"
                          : "Activez pour recevoir des colis dans votre magasin"}
                      </span>
                    </div>

                    {/* Switch — stopPropagation prevents double-toggle */}
                    <label className="sw" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        name="ReceptionClois"
                        checked={ReceptionClois}
                        onChange={() => setReceptionClois(!ReceptionClois)}
                      />
                      <span className="sw-track" />
                    </label>
                  </div>
                </div>

              </div>{/* end grid */}

              <button 
                type="submit" 
                style={{ ...s.submitButton, opacity: sendingOtp ? 0.7 : 1, cursor: sendingOtp ? "not-allowed" : "pointer" }} 
                disabled={sendingOtp}
              >
                {sendingOtp ? <span className="magasin-loader-spinner" /> : "Créer mon magasin"}
              </button>

              <div style={s.linkContainer}>
                <span style={{ color: "#6b7280", marginRight: "8px" }}>
                  Vous avez déjà un compte ?
                </span>
                <Link to="/" style={s.link}>Se connecter</Link>
              </div>
            </form>

          </div>
        </div>
      </div>
    </>
  );
};

export default CreateMagasiEnligne;